// src/components/gestionEvento/Ajustes/Competidores/Inscripciones/sidebar/TeamSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';

const toText = (v, fallback = '—') => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.nombre ?? v.name ?? v.label ?? fallback;
  return String(v ?? fallback);
};

function TeamSection({ value, readOnly, onChange, categorias, onTeamChange }) {
  const ramaValue = value?.rama ?? '';

  // Normaliza el value del select de categoría (usa id o catNumber)
  const categoriaValue = categorias?.length
    ? String(value?.categoria?.id ?? value?.categoria?.catNumber ?? value?.categoria ?? '')
    : '';

  const teamObj = typeof value?.equipo === 'object' ? value.equipo : null;
  const teamName = teamObj?.name ?? (typeof value?.equipo === 'string' ? value.equipo : '');
  const teamLogo = teamObj?.logoEquipo ?? '';

  if (readOnly) {
    return (
      <Stack spacing={1.25}>
        <Typography variant="overline" color="text.secondary">Equipo</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            src={teamLogo || undefined}
            alt={toText(teamName, '')}
            variant="square"
            sx={{ width: 32, height: 32 }}
          >
            {!teamLogo ? (teamName?.[0]?.toUpperCase() || '') : null}
          </Avatar>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {toText(teamName)}
          </Typography>
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Rama</Typography>
        <Typography variant="body2">{toText(ramaValue)}</Typography>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Categoría</Typography>
        <Typography variant="body2">
          {toText(value?.categoria?.nombre ?? value?.categoria)}
        </Typography>
      </Stack>
    );
  }

  // Modo edición
  return (
    <Stack spacing={2}>
      {/* Equipo */}
      <Box>
        <Typography variant="overline" color="text.secondary">Equipo</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          <Avatar
            src={teamLogo || undefined}
            alt={toText(teamName, '')}
            variant="square"
            sx={{ width: 32, height: 32 }}
          >
            {!teamLogo ? (teamName?.[0]?.toUpperCase() || '') : null}
          </Avatar>

          <TextField
            size="small"
            label="Nombre del equipo"
            value={teamName}
            onChange={(e) => {
              const name = e.target.value;
              // Cuando el equipo era string, lo dejamos como string al teclear
              onChange?.((p) => ({ ...p, equipo: name }));
            }}
            onBlur={(e) => {
              const name = e.target.value?.trim();
              if (name) onTeamChange?.({ name });
            }}
            sx={{ minWidth: 260 }}
          />
        </Stack>
      </Box>

      {/* Rama */}
      <Box>
        <Typography variant="overline" color="text.secondary">Rama</Typography>
        <Select
          size="small"
          value={ramaValue || ''}
          onChange={(e) => {
            const val = e.target.value;
            onChange?.((p) => ({ ...p, rama: val }));
          }}
          displayEmpty
          sx={{ mt: 0.5, minWidth: 200 }}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          <MenuItem value="Femenil">Femenil</MenuItem>
          <MenuItem value="Varonil">Varonil</MenuItem>
          <MenuItem value="Mixto">Mixto</MenuItem>
        </Select>
      </Box>

      {/* Categoría */}
      <Box>
        <Typography variant="overline" color="text.secondary">Categoría</Typography>
        <Select
          size="small"
          value={categorias?.length ? categoriaValue : ''}
          onChange={(e) => {
            const val = String(e.target.value);
            const found =
              categorias.find((c) => String(c.id) === val) ||
              categorias.find((c) => String(c.catNumber) === val);

            if (found) {
              onChange?.((p) => ({
                ...p,
                categoria: { id: found.id, nombre: found.nombre, catNumber: found.catNumber },
              }));
            } else {
              onChange?.((p) => ({ ...p, categoria: '' }));
            }
          }}
          displayEmpty
          sx={{ mt: 0.5, minWidth: 240 }}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {(categorias || []).map((c) => (
            <MenuItem key={c.id || c.nombre || c.catNumber} value={String(c.id ?? c.catNumber)}>
              {c.nombre}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  );
}

TeamSection.propTypes = {
  value: PropTypes.object,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  categorias: PropTypes.array,
  onTeamChange: PropTypes.func,
};

export default TeamSection;
